import { Flex, Heading, VStack, Text } from '@chakra-ui/react'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { AxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'

import { ActionFab } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { getApiErrorMessage, useHasRole, useAppToast, usePendingRows } from '../../../../helpers'
import { IBannerData, IBannerRequest } from '../../../../interfaces/Website/Banners/IBanners'
import BannerService from '../../../../services/Website/BannerService'
import { useBannerFonts } from '../useBannerFonts'
import BannerPageSortableBanner from './BannerPageSortableBanner'
import { bannerSignature, isPersistedBanner, nextTemporaryBannerId, runWithConcurrency } from './helpers'

// Uploads em voo por vez ao salvar. O `throttle` da API é por minuto, então o que
// estourava o limite era a rajada simultânea, não o total: 3 por vez mantém o save
// rápido sem despejar a home inteira de uma vez.
const SAVE_CONCURRENCY = 3

type ISaveOutcome =
  | { index: number; status: 'saved'; id: number }
  | { index: number; status: 'invalid'; fieldErrors: Record<keyof IBannerRequest, string[]> }
  | { index: number; status: 'failed'; error: unknown }

/**
 * O motivo de um save que falhou, para a descrição do toast. Num 500 a API de produção só
 * responde `Server Error` (sem `APP_DEBUG`), e o `getApiErrorMessage` descarta essa frase: o que
 * sobra de concreto é o status, que é o que distingue um erro do servidor de um 429 ou de um 403.
 */
function saveFailureReason(error: unknown): string {
  const message = getApiErrorMessage(error)
  if (message) return message

  const status = (error as AxiosError | undefined)?.response?.status
  return status
    ? `O servidor respondeu com erro ${status}. Tente novamente mais tarde.`
    : 'Erro inesperado ao salvar. Tente novamente mais tarde.'
}

/** Estado dos banners como o servidor os devolveu, para saber o que mudou desde então. */
interface ISavedState {
  banners: Record<number, IBannerData>
  order: number[]
}

function BannerPage({ bannerFonts }: { bannerFonts: ReturnType<typeof useBannerFonts> }) {
  const service = new BannerService()
  const toast = useAppToast()
  const [banners, setBanners] = useState<IBannerData[]>([])
  const [errors, setErrors] = useState<Record<keyof IBannerRequest, string[]>[]>([])
  const [deleteIndex, setDeleteIndex] = useState<number | null>()
  const [isSaving, setIsSaving] = useState(false)
  const savedState = useRef<ISavedState>({ banners: {}, order: [] })
  const hasRole = useHasRole()
  const permissions = {
    addBanner: hasRole('add_banner'),
    editBanner: hasRole('edit_banner')
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over) return

    if (active.id !== over.id) {
      setBanners((items) => {
        const oldIndex = items.findIndex((item) => item.id.toString() === active.id)
        const newIndex = items.findIndex((item) => item.id.toString() === over.id)

        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const fetchBanners = async () => {
    try {
      const response = await service.list()

      const sortedBanners = response.data.sort((a: IBannerData, b: IBannerData) => {
        const aActive = !!a.is_active
        const bActive = !!b.is_active

        if (aActive === bActive) return 0
        return aActive ? -1 : 1
      })

      setBanners(sortedBanners)
      savedState.current = {
        banners: Object.fromEntries(sortedBanners.map((banner: IBannerData) => [banner.id, banner])),
        // Ordem exibida, não a do servidor: `sortedBanners` já sobe os ativos, e é essa
        // ordem que o `reorder` persiste ao salvar.
        order: sortedBanners.map((banner: IBannerData) => banner.id)
      }
    } catch (error) {
      toast.error('Erro ao buscar banners', error)
    }
  }

  function addBanner() {
    const newBanner: Omit<IBannerData, 'id'> = {
      title: '',
      subtitle: '',
      text: '',
      image: null,
      image_mobile: null,
      video_url: '',
      video_url_mobile: '',
      cta_text: '',
      cta_link: '',
      is_active: true,
      title_color: '',
      subtitle_color: '',
      tag_presencial_color: '',
      tag_online_color: '',
      tag_online_ao_vivo_color: '',
      tag_presencial_text_color: '',
      tag_online_text_color: '',
      tag_online_ao_vivo_text_color: '',
      button_text_color: '',
      gradient_color: '',
      is_gradient_active: 1,
      button_color: '',
      tags: []
    }

    // O id sai de dentro do updater para ler a lista já atualizada: calculado de fora,
    // dois cliques na mesma renderização gerariam o mesmo id temporário.
    setBanners((prevBanners) => [...prevBanners, { ...newBanner, id: nextTemporaryBannerId(prevBanners) }])

    // Scroll até o novo banner após a renderização
    setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth'
      })
    }, 100)
  }

  function repositionBanner(fromIndex: number, toIndex: number) {
    const newArray = [...banners]

    if (fromIndex < 0 || fromIndex >= newArray.length || toIndex < 0 || toIndex >= newArray.length) {
      return
    }

    const [movedItem] = newArray.splice(fromIndex, 1)
    newArray.splice(toIndex, 0, movedItem)
    setBanners(newArray)
  }

  const handleDelete = async () => {
    try {
      if (deleteIndex == null) return
      const deletedId = banners[deleteIndex].id
      if (isPersistedBanner(deletedId)) {
        await service.delete(deletedId)
        delete savedState.current.banners[deletedId]
        savedState.current.order = savedState.current.order.filter((id) => id !== deletedId)
      }
      const newBanners = [...banners]
      newBanners.splice(deleteIndex, 1)
      setBanners(newBanners)
      // `errors` é indexado por posição: sem tirar a mesma linha, os erros de validação
      // dos banners abaixo do excluído passam a ser exibidos no card errado.
      setErrors((prevErrors) => {
        const newErrors = [...prevErrors]
        newErrors.splice(deleteIndex, 1)
        return newErrors
      })

      toast.success('Banner excluído')

      setDeleteIndex(null)
    } catch (error) {
      toast.error('Erro ao excluir banner', error)
    }
  }

  const savingBanner = usePendingRows()

  const handleSwitch = async (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    try {
      const shouldEnableBanner = e.target.checked

      if (shouldEnableBanner) {
        await service.enable(id)
      } else {
        await service.disable(id)
      }

      // O endpoint grava só `is_active`. A referência salva não pode copiar o rascunho
      // local: título, cores e mídia ainda precisam ser enviados por "Salvar alterações".
      const persisted = savedState.current.banners[id]
      if (persisted) {
        savedState.current.banners[id] = { ...persisted, is_active: shouldEnableBanner }
      }

      setBanners((prevBanners) =>
        prevBanners.map((banner) => (banner.id === id ? { ...banner, is_active: shouldEnableBanner } : banner))
      )

      toast.success(`Banner ${shouldEnableBanner ? 'ativado' : 'desativado'} com sucesso`)
    } catch (err) {
      toast.error('Erro ao atualizar banner', err)
    }
  }

  useEffect(() => {
    fetchBanners()
  }, [])

  const openDeleteDialog = (id: number) => {
    setDeleteIndex(id)
  }

  const handleSubmit = async () => {
    if (isSaving) return

    const snapshot = savedState.current
    const displayedOrder = banners.map(({ id }) => id)
    const orderChanged =
      displayedOrder.length !== snapshot.order.length ||
      displayedOrder.some((id, index) => id !== snapshot.order[index])

    // O ponto do 429: a tela reenviava TODOS os banners a cada clique, um multipart
    // cada. Salvar um banner agora é uma request, não vinte.
    const pending = banners
      .map((banner, index) => ({ banner, index }))
      .filter(
        ({ banner }) =>
          !isPersistedBanner(banner.id) ||
          !snapshot.banners[banner.id] ||
          bannerSignature(snapshot.banners[banner.id]) !== bannerSignature(banner)
      )

    if (!pending.length && !orderChanged) {
      toast.info('Nenhuma alteração para salvar')
      return
    }

    setIsSaving(true)

    try {
      const outcomes = await runWithConcurrency<ISaveOutcome>(
        pending.map(({ banner, index }) => async () => {
          const { id, ...fields } = banner
          const bannerToSend: IBannerRequest & { is_active: number; image?: File; image_mobile?: File } = {
            ...fields,
            is_active: fields.is_active ? 1 : 0,
            // Só sobe arquivo novo: com a imagem ainda na URL vinda do servidor o campo
            // fica fora do FormData e o back-end mantém a que já está lá.
            image: fields.image instanceof File ? fields.image : undefined,
            image_mobile: fields.image_mobile instanceof File ? fields.image_mobile : undefined
          }

          try {
            if (isPersistedBanner(id)) {
              await service.update(id, bannerToSend)
              return { index, status: 'saved', id }
            }

            const { data } = await service.create(bannerToSend)
            return { index, status: 'saved', id: data.id }
          } catch (err) {
            const error = err as AxiosError<{ errors: Record<keyof IBannerRequest, string[]> }>
            const fieldErrors = error.response?.data?.errors

            if (fieldErrors && (error.response?.status === 400 || error.response?.status === 422)) {
              return { index, status: 'invalid', fieldErrors }
            }

            return { index, status: 'failed', error: err }
          }
        }),
        SAVE_CONCURRENCY
      )

      const nextErrors = banners.map(() => ({}) as Record<keyof IBannerRequest, string[]>)
      const savedIds: Record<number, number> = {}
      let invalidCount = 0
      let failedCount = 0
      let firstFailure: unknown

      outcomes.forEach((outcome) => {
        if (outcome.status === 'saved') {
          savedIds[outcome.index] = outcome.id
        } else if (outcome.status === 'invalid') {
          nextErrors[outcome.index] = outcome.fieldErrors
          invalidCount += 1
        } else {
          if (!failedCount) firstFailure = outcome.error
          failedCount += 1
        }
      })

      // Array novo: o código anterior mutava o anterior e devolvia a mesma referência,
      // então o React não re-renderizava e os erros de validação não apareciam.
      setErrors(nextErrors)

      const failures = invalidCount + failedCount
      if (failures) {
        // Um toast só. Antes era um por banner, e a rajada de 429 chegava na tela como
        // uma pilha de alertas idênticos.
        // A falha do servidor vem antes da de validação: os campos recusados já ficam
        // destacados no card, e o motivo de um 500 ou de um 429 não aparece em lugar nenhum.
        toast.error(
          failures === 1 ? 'Um banner não foi salvo' : `${failures} banners não foram salvos`,
          failedCount ? saveFailureReason(firstFailure) : 'Confira os campos destacados e tente novamente.'
        )

        // Os que passaram já existem no servidor: sem gravar o id aqui, um segundo
        // clique em salvar criaria duplicatas deles. E sem gravar a assinatura junto,
        // eles voltariam como sujos na próxima tentativa e seriam reenviados com os que
        // falharam — o oposto do que esta tela passou a fazer.
        Object.entries(savedIds).forEach(([index, savedId]) => {
          savedState.current.banners[savedId] = { ...banners[Number(index)], id: savedId }
        })
        // `order` fica como está de propósito: o `reorder` não rodou, então a ordem
        // exibida segue divergindo da persistida e precisa continuar contando como
        // alteração na próxima tentativa.
        setBanners((prevBanners) =>
          prevBanners.map((banner, index) => (savedIds[index] ? { ...banner, id: savedIds[index] } : banner))
        )
        return
      }

      await service.reorder(displayedOrder.map((id, index) => savedIds[index] ?? id))
      await fetchBanners()

      toast.success('Alterações salvas', 'Os banners da home foram atualizados.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      {/* h2, não PageTitle: esta é a aba de dentro de BannersPage, que já rende o h1 da
          página. O texto acompanha o rótulo da aba, como em SidebarBannerPage. */}
      <Flex justify="space-between" align="center" mb={6}>
        <Heading as="h2">Banners Home</Heading>
      </Flex>

      {bannerFonts.isLoaded && !banners.length && <Text>Não há banners cadastrados</Text>}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={banners.map((banner) => banner.id.toString())} strategy={verticalListSortingStrategy}>
          <VStack spacing={10} align="stretch" width="100%">
            {/* Os cards só montam com a lista de fontes em mãos: o editor lê a whitelist ao
                interpretar o HTML, e um banner com fonte enviada abriria sem ela (ver `setBannerFonts`). */}
            {bannerFonts.isLoaded &&
              banners.map((banner, i) => (
                <BannerPageSortableBanner
                  key={banner.id}
                  id={banner.id.toString()}
                  data={banner}
                  fonts={bannerFonts.fonts}
                  onChange={(data) => {
                    setBanners((_banners) => {
                      const newBanners = [..._banners]
                      newBanners[i] = data
                      return newBanners
                    })
                  }}
                  errors={errors[i]}
                  changePosition={(direction) => repositionBanner(i, i + (direction === 'up' ? -1 : 1))}
                  openDeleteDialog={() => openDeleteDialog(i)}
                  handleSwitch={(e, id) => savingBanner.run(id, () => handleSwitch(e, id))}
                  isSwitchSaving={(id) => isSaving || savingBanner.isPending(id)}
                />
              ))}
          </VStack>
        </SortableContext>
      </DndContext>

      <ConfirmDialog
        isOpen={deleteIndex != null}
        onClose={() => setDeleteIndex(null)}
        onConfirm={handleDelete}
        action="delete"
        entity="banner"
      />
      {/* add button */}
      {permissions.addBanner && <ActionFab label="Adicionar banner" onClick={addBanner} isDisabled={isSaving} />}

      {(permissions.addBanner || permissions.editBanner) && (
        <ActionFab
          action="approve"
          label="Salvar alterações"
          stackIndex={1}
          onClick={handleSubmit}
          isLoading={isSaving}
        />
      )}
    </>
  )
}

export default BannerPage
